/**
 * Système de gestion du panier unifié
 * Architecture simplifiée avec un seul point de refresh centralisé
 */

/**
 * Fonction autonome de refresh du panier drawer
 * @param {string|null} sectionHTML - HTML de la section fourni par l'API Section Rendering (optionnel)
 */
async function refreshCartDrawer(sectionHTML = null) {
  try {
    let html;

    if (sectionHTML) {
      html = sectionHTML;
    } else {
      const response = await fetch(
        `${window.Shopify.routes.root}cart?sections=cart-drawer&section_id=cart-drawer`,
      );
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      const data = await response.json();
      html = data["cart-drawer"];
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const newItems = doc.querySelector("[data-products]");
    const newSubtotal = doc.querySelector("[data-subtotal]");
    const newTotal = doc.querySelector("[data-cart-total-price]");
    const newDiscounts = doc.querySelector(".cart__discounts");
    const newDiscountsApplied = doc.querySelector(".cart__discounts-applied");
    const newCompareAtPrice = doc.querySelector(
      ".cart__checkout-button-savings",
    );
    const newGamification = doc.querySelector("cart-gamification");
    const checkoutCTA = doc.querySelector("button[name='checkout']");
    const currentCheckoutCTA = document.querySelector(
      "button[name='checkout']",
    );

    const currentShipppingEstimator = doc.querySelector(".cart__shipping");
    const newShippingEstimator = document.querySelector(".cart__shipping");

    if (!newItems) {
      return;
    }

    const count = parseInt(newItems.dataset.count) || 0;
    const subtotal = newItems.dataset.cartSubtotal;
    const savings = newItems.dataset.cartSavings;

    const form = document.querySelector('[data-location="drawer"]');
    if (!form) return;

    const wrapper = form.parentNode;
    wrapper.classList.toggle("is-empty", count === 0);

    if (checkoutCTA && currentCheckoutCTA) {
      currentCheckoutCTA.disabled = checkoutCTA.disabled;
      currentCheckoutCTA.className = checkoutCTA.className;
      currentCheckoutCTA.innerHTML = checkoutCTA.innerHTML;
    }

    if (currentShipppingEstimator && newShippingEstimator) {
      newShippingEstimator.innerHTML = currentShipppingEstimator.innerHTML;
    }

    const currentItems = form.querySelector("[data-products]");
    if (currentItems && newItems) {
      currentItems.innerHTML = "";
      currentItems.append(newItems);
    }

    const currentSubtotal = form.querySelector("[data-subtotal]");
    if (currentSubtotal && newSubtotal) {
      currentSubtotal.innerHTML = newSubtotal.innerHTML;
    }

    const currentTotal = form.querySelector("[data-cart-total-price]");
    if (currentTotal && newTotal) {
      currentTotal.innerHTML = newTotal.innerHTML;
    }

    const currentDiscounts = form.querySelector("[data-discounts]");
    if (currentDiscounts && newDiscounts) {
      currentDiscounts.innerHTML = "";
      currentDiscounts.append(newDiscounts);
    }

    const currentDiscountsApplied = form.querySelector(
      ".cart__discounts-applied",
    );
    if (currentDiscountsApplied && newDiscountsApplied) {
      if (newDiscountsApplied.classList.contains("hidden")) {
        currentDiscountsApplied.classList.add("hidden");
      } else {
        currentDiscountsApplied.classList.remove("hidden");
      }
      currentDiscountsApplied.innerHTML = newDiscountsApplied.innerHTML;
    }

    const currentCompareAtPrice = form.querySelector(
      ".cart__checkout-button-savings",
    );

    if (currentCompareAtPrice && newCompareAtPrice) {
      currentCompareAtPrice.innerHTML = newCompareAtPrice.innerHTML;

      const totalValue = parseInt(currentTotal?.innerHTML || "0");
      const compareValue = parseInt(newCompareAtPrice.innerHTML || "0");

      if (totalValue < compareValue) {
        currentCompareAtPrice.classList.remove("hidden");
      } else {
        currentCompareAtPrice.classList.add("hidden");
      }
    } else if (currentCompareAtPrice) {
      currentCompareAtPrice.classList.add("hidden");
    }

    const currentSavings = form.querySelector("[data-savings]");
    if (currentSavings && savings) {
      if (parseInt(savings) > 0) {
        const amount = theme.Currency.formatMoney(
          savings,
          theme.settings.moneyFormat,
        );
        currentSavings.classList.remove("hide");
        currentSavings.innerHTML = theme.strings.cartSavings.replace(
          "[savings]",
          amount,
        );
      } else {
        currentSavings.classList.add("hide");
      }
    }

    const countEls = document.querySelectorAll(".cart-link__bubble-num");
    for (const el of countEls) {
      el.innerText = count;
    }

    const bubbles = document.querySelectorAll(".cart-link__bubble");
    for (const bubble of bubbles) {
      if (count > 0) {
        bubble.classList.add("cart-link__bubble--visible");
      } else {
        bubble.classList.remove("cart-link__bubble--visible");
      }
    }

    // if (window.AOS) {
    //   AOS.refreshHard();
    // }

    if (window.Shopify?.StorefrontExpressButtons) {
      window.Shopify.StorefrontExpressButtons.initialize();
    }

    const discountError = form.querySelector("[data-discount-error]");
    if (discountError) {
      discountError.textContent = "";
      discountError.setAttribute("hidden", "");
    }

    const currentGamification = document.querySelector("cart-gamification");
    if (currentGamification && newGamification) {
      currentGamification.outerHTML = newGamification.outerHTML;
    }
  } catch (error) {
    console.error("Erreur lors du refresh du panier:", error);
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = null;
    this.drawerInstance = null;
    this.isInitialized = false;

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    this.drawer =
      this.querySelector("#CartDrawer") ||
      document.getElementById("CartDrawer");

    if (!this.drawer) return;

    if (window.theme && window.theme.Drawers) {
      this.drawerInstance = new window.theme.Drawers("CartDrawer", "cart");
    }

    this.setupEventListeners();
    this.isInitialized = true;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("open_cart") && urlParams.get("open_cart") === "true") {
      this.open();
      urlParams.delete("open_cart");
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams.toString()}`,
      );
    }
  }

  setupEventListeners() {
    document.addEventListener(
      "ajaxProduct:added",
      this.handleProductAdded.bind(this),
    );

    this.addEventListener("keyup", (event) => {
      if (event.code && event.code.toUpperCase() === "ESCAPE") {
        this.close();
      }
    });

    document.addEventListener(
      "cart:updated",
      this.handleCartUpdated.bind(this),
    );
  }

  async handleProductAdded(event) {
    const wasOpen = this.isOpen();

    const openModals = document.querySelectorAll(".modal.modal--is-active");
    openModals.forEach((modal) => {
      if (
        modal._modalInstance &&
        typeof modal._modalInstance.close === "function"
      ) {
        modal._modalInstance.close();
      }
    });

    await refreshCartDrawer();

    document.dispatchEvent(new CustomEvent("product:added-from-modal"));

    if (!wasOpen) {
      this.open();
    }
  }

  handleCartUpdated(event) {
    if (!event.detail || !event.detail.cart) return;

    const isEmpty = event.detail.cart.item_count === 0;
    this.classList.toggle("is-empty", isEmpty);
  }

  open() {
    if (!this.drawer) {
      this.init();
      if (!this.drawer) return;
    }

    if (this.drawerInstance && this.drawerInstance.open) {
      this.drawerInstance.open();
    } else {
      this.drawer.classList.add("drawer--is-open");
      document.body.classList.add("drawer-open");
    }

    if (window.trapFocus) {
      window.trapFocus(
        this.drawer,
        this.drawer.querySelector('button[type="button"]'),
      );
    }

    this.setAttribute("aria-hidden", "false");

    document.dispatchEvent(new CustomEvent("cart-drawer:opened"));
  }

  close() {
    if (!this.drawer) return;

    if (this.drawerInstance && this.drawerInstance.close) {
      this.drawerInstance.close();
    } else {
      this.drawer.classList.remove("drawer--is-open");
      document.body.classList.remove("drawer-open");
    }

    if (window.removeTrapFocus) {
      const focusElement = document.querySelector(".js-drawer-open-cart");
      window.removeTrapFocus(focusElement);
    }

    this.setAttribute("aria-hidden", "true");

    document.dispatchEvent(new CustomEvent("cart-drawer:closed"));
  }

  async refresh() {
    await refreshCartDrawer();
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains("drawer--is-open");
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class CartDiscountCode extends HTMLElement {
  constructor() {
    super();

    this.input = null;
    this.button = null;
    this.errorElement = null;
    this.isApplying = false;

    this.init();
  }

  init() {
    this.input = this.querySelector("[data-discount-input]");
    this.button = this.querySelector("[data-discount-button]");
    this.errorElement = this.querySelector("[data-discount-error]");

    if (!this.input || !this.button) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.button.addEventListener("click", () => this.applyDiscount());
    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        this.applyDiscount();
      }
    });
  }

  async applyDiscount() {
    const code = this.input.value.trim();
    if (!code || this.isApplying) return;

    this.isApplying = true;
    this.setLoadingState(true);
    this.hideError();

    try {
      const existingDiscounts = this.getExistingDiscounts();
      const newDiscounts =
        existingDiscounts.length > 0 ? `${existingDiscounts},${code}` : code;

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discount: newDiscounts,
          sections: "cart-drawer",
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const data = await response.json();

      const wasApplied = data.discount_codes?.some((discount) => {
        return (
          discount.code &&
          discount.code.toLowerCase() === code.toLowerCase() &&
          discount.applicable === true
        );
      });

      if (wasApplied) {
        this.input.value = "";
        if (data.sections && data.sections["cart-drawer"]) {
          await refreshCartDrawer(data.sections["cart-drawer"]);
        } else {
          await this.refreshCart();
        }
      } else {
        this.showError(
          "Le contenu de votre panier ne correspond pas à ce code de réduction, ou le code est invalide.",
        );
      }
    } catch (error) {
      this.showError("Erreur lors de l'application du code");
    } finally {
      this.isApplying = false;
      this.setLoadingState(false);
    }
  }

  getExistingDiscounts() {
    const discountElements = document.querySelectorAll("[data-discount-code]");
    return Array.from(discountElements)
      .map((el) => el.getAttribute("data-discount-code"))
      .filter((code) => code)
      .join(",");
  }

  async refreshCart() {
    await refreshCartDrawer();
  }

  setLoadingState(loading) {
    this.input.disabled = loading;
    this.button.disabled = loading;

    const cartItems = document.querySelector("cart-items");
    if (cartItems) {
      cartItems.classList.toggle("loading", loading);
    }

    this.input.classList.toggle("is-loading", loading);
    this.button.classList.toggle("is-loading", loading);
  }

  showError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (!this.errorElement) return;

    this.errorElement.textContent = "";
    this.errorElement.hidden = true;
  }
}

class CartDiscountRemover extends HTMLElement {
  constructor() {
    super();

    this.button = null;
    this.isRemoving = false;

    this.init();
  }

  init() {
    this.button = this.querySelector("[data-remove-discount]");
    if (!this.button) return;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.button.addEventListener("click", () => this.removeDiscount());
  }

  async removeDiscount() {
    if (this.isRemoving) return;

    const discountCode = this.button.getAttribute("data-discount-code");
    if (!discountCode) return;

    this.isRemoving = true;
    this.setLoadingState(true);

    try {
      const allDiscountElements = document.querySelectorAll(
        "[data-discount-code]",
      );
      const remainingDiscounts = Array.from(allDiscountElements)
        .map((el) => el.getAttribute("data-discount-code"))
        .filter((code) => code && code !== discountCode)
        .join(",");

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discount: remainingDiscounts,
          sections: "cart-drawer",
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const data = await response.json();

      if (data.sections && data.sections["cart-drawer"]) {
        await refreshCartDrawer(data.sections["cart-drawer"]);
      } else {
        await this.refreshCart();
      }
    } catch (error) {
    } finally {
      this.isRemoving = false;
      this.setLoadingState(false);
    }
  }

  async refreshCart() {
    await refreshCartDrawer();
  }

  setLoadingState(loading) {
    const cartItems = document.querySelector("cart-items");
    if (cartItems) {
      cartItems.classList.toggle("loading", loading);
    }

    this.button.classList.toggle("is-loading", loading);
  }
}

if (!customElements.get("cart-discount-code")) {
  customElements.define("cart-discount-code", CartDiscountCode);
}

if (!customElements.get("cart-discount-remover")) {
  customElements.define("cart-discount-remover", CartDiscountRemover);
}

class CartSubscriptionSelector extends HTMLElement {
  constructor() {
    super();

    this.itemKey = this.dataset.itemKey;
    this.variantId = this.dataset.variantId;
    this.isUpdating = false;

    this.initEventListeners();
  }

  initEventListeners() {
    const toggleCheckbox = this.querySelector(
      ".cart-item__subscription-checkbox-input",
    );
    if (toggleCheckbox) {
      toggleCheckbox.addEventListener(
        "change",
        this.handleToggleChange.bind(this),
      );
    }

    const planRadios = this.querySelectorAll(
      ".cart-item__subscription-option-input",
    );
    for (const radio of planRadios) {
      radio.addEventListener("change", this.handleFrequencyChange.bind(this));
    }
  }

  async handleToggleChange(event) {
    if (this.isUpdating) return;

    const isChecked = event.target.checked;

    if (!isChecked) {
      await this.removeSubscription();
    } else {
      await this.activateSubscription();
    }
  }

  async handleFrequencyChange(event) {
    if (this.isUpdating) return;

    const sellingPlanId = parseInt(event.target.value, 10);
    await this.updateSubscriptionPlan(sellingPlanId);
  }

  async removeSubscription() {
    await this.updateCart({
      id: this.itemKey,
      selling_plan: null,
    });
  }

  async activateSubscription() {
    const firstPlanRadio = this.querySelector(
      ".cart-item__subscription-option-input",
    );

    if (!firstPlanRadio) {
      this.showError("Aucun plan d'abonnement disponible");
      return;
    }

    const sellingPlanId = parseInt(firstPlanRadio.value, 10);
    await this.updateCart({
      id: this.itemKey,
      selling_plan: sellingPlanId,
    });
  }

  async updateSubscriptionPlan(sellingPlanId) {
    await this.updateCart({
      id: this.itemKey,
      selling_plan: sellingPlanId,
    });
  }

  async updateCart(data) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.setLoadingState(true);

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          ...data,
          sections: "cart-drawer",
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Erreur lors de la mise à jour du panier",
        );
      }

      const cart = await response.json();
      await this.dispatchCartUpdate(cart);
    } catch (error) {
      this.showError(error.message);
      this.revertChanges();
    } finally {
      this.isUpdating = false;
      this.setLoadingState(false);
    }
  }

  async dispatchCartUpdate(cart) {
    if (cart.sections && cart.sections["cart-drawer"]) {
      await refreshCartDrawer(cart.sections["cart-drawer"]);
    } else {
      await refreshCartDrawer();
    }

    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: {
          cart: cart,
        },
      }),
    );
  }

  setLoadingState(loading) {
    const cartItems = document.querySelector("cart-items");
    if (cartItems) {
      cartItems.classList.toggle("loading", loading);
    }
  }

  revertChanges() {
    const checkedInputs = this.querySelectorAll("input:checked");
    for (const input of checkedInputs) {
      input.checked = false;
    }
  }

  showError(message) {
    document.dispatchEvent(
      new CustomEvent("cart:error", {
        detail: { message },
        bubbles: true,
      }),
    );
  }
}

customElements.define("cart-subscription-selector", CartSubscriptionSelector);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.querySelector(".cart-item-status");

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]'),
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0,
    );

    this.debouncedOnChange = this.debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener("change", this.debouncedOnChange.bind(this));
    this.addEventListener("click", this.onButtonClick.bind(this));
  }

  async onButtonClick(event) {
    const target = event.target.closest(
      '[data-action="minus"], [data-action="plus"], .cart-item__remove',
    );
    if (!target) return;

    event.preventDefault();

    const index = target.dataset.index;
    if (!index) return;

    if (target.classList.contains("cart-item__remove")) {
      await this.updateQuantity(index, 0);

      if (target.dataset.shouldDisableRemove === "true") {
        const removeButton = document.querySelector(
          '[data-add-to-cart][data-should-disable-add-to-cart="true"]',
        );
        if (removeButton) {
          removeButton.removeAttribute("disabled");
          removeButton.dataset.shouldDisableAddToCart = "false";
        }
      }
    } else {
      const input = this.querySelector(
        `[data-index="${index}"].cart-item__quantity-input`,
      );
      if (!input) return;
      const currentValue = parseInt(input.value) || 0;
      const action = target.dataset.action;
      const newValue =
        action === "plus" ? currentValue + 1 : Math.max(0, currentValue - 1);
      input.value = newValue;
      await this.updateQuantity(index, newValue);
    }
  }

  async onChange(event) {
    const input = event.target;
    if (input.name !== "updates[]") return;

    const index = input.dataset.index;
    if (!index) return;

    try {
      await this.updateQuantity(index, input.value);
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateQuantity(line, quantity) {
    this.setLoadingState(true);

    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: "cart-drawer",
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      this.closest("cart-drawer").classList.toggle(
        "is-empty",
        data.item_count === 0,
      );

      this.updateLiveRegions(this.currentItemCount, data.item_count);
      this.currentItemCount = data.item_count;

      if (data.sections && data.sections["cart-drawer"]) {
        await refreshCartDrawer(data.sections["cart-drawer"]);
      } else {
        await refreshCartDrawer();
      }

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart: data },
        }),
      );
    } catch (error) {
      this.handleError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  updateLiveRegions(previousItemCount, newItemCount) {
    if (!this.lineItemStatusElement) return;

    if (previousItemCount === newItemCount) {
      this.lineItemStatusElement.setAttribute("aria-hidden", true);
      return;
    }

    this.lineItemStatusElement.setAttribute("aria-hidden", false);
    this.lineItemStatusElement.removeAttribute("role");

    setTimeout(() => {
      this.lineItemStatusElement.setAttribute("role", "status");
    });
  }

  setLoadingState(loading) {
    this.classList.toggle("loading", loading);

    if (loading) {
      document.activeElement.blur();
    }
  }

  handleError(error) {
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", false);
      this.lineItemStatusElement.textContent =
        error.message || "Une erreur est survenue";
    }
  }

  debounce(fn, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        fn(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}
